import { Toast as toast } from 'components'
import React, { useCallback, useEffect, useState } from 'react'
import { isCarrierLogin, isAgentLogin, isViewOnly } from 'tools/auth'
import { reportException } from 'tools/logs'
import RadiusOfOperationFilter from './radiusOfOperationFilter'
import './radiusOfOperation.module.scss'
import GaragingZipcodeConfig from './garagingZipcodeConfig'
import { getGaragingZipcodeConfigs, updateGaragingZipcodeConfigs } from 'api'
import { REPORT_KEYS } from 'constants/reporting'
import ConsoleEmbed from 'components/ConsoleEmbed'
import { useEnsureInvitingCompanyId } from 'hooks/useEnsureInvitingCompanyId'
import { useCarrierCodeBootstrap } from 'hooks/useCarrierCodeBootstrap'

interface IRadiusOfOperationProps {
  print?: boolean
  useCarrierFilters: boolean
}

const RadiusOfOperationDashboard: React.FC<IRadiusOfOperationProps> = (
  props
) => {
  const { useCarrierFilters } = props
  const ensuredInvitingCompanyId = useEnsureInvitingCompanyId()
  const carrierData = useCarrierCodeBootstrap()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [gzcData, setGzcData] = useState<any>([])
  const [currentData, setCurrentData] = useState<any>([])
  const [saveBtnLoader, setSaveBtnLoader] = useState(false)
  const [embedKey, setEmbedKey] = useState(0)

  useEffect(() => {
    callGetGzc()
  }, [])

  const callGetGzc = useCallback(async () => {
    setLoading(true)
    getGaragingZipcodeConfigs(
      localStorage.getItem('carrierId') ?? '',
      localStorage.getItem('carrierCodeType') ?? '',
      localStorage.getItem('carrierCodeValue') ?? ''
    )
      .then((res: any) => {
        setGzcData(res)
        setCurrentData(res)
      })
      .catch((error: any) => {
        reportException(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  const callUpdateGzc = useCallback(async () => {
    setSaveBtnLoader(true)
    const payload = currentData
      .filter((row: any) => {
        const originalRow = gzcData.find(
          (asset: any) => asset.assetEldId === row.assetEldId
        )
        return originalRow && originalRow.manualGzc !== row.manualGzc
      })
      .map((row: any) => ({
        assetEldId: row.assetEldId,
        manualGzc: row.manualGzc,
      }))

    updateGaragingZipcodeConfigs(
      payload,
      localStorage.getItem('carrierId') ?? '',
      localStorage.getItem('carrierCodeType') ?? '',
      localStorage.getItem('carrierCodeValue') ?? ''
    )
      .then((res: any) => {
        toast({ id: 'updateGzc', type: 'success', subTitle: res?.message })
      })
      .catch((error: any) => {
        reportException(error)
      })
      .finally(() => {
        setSaveBtnLoader(false)
        setIsDialogOpen(false)
        callGetGzc()
      })
  }, [currentData, gzcData])

  const handleSave = () => {
    callUpdateGzc()
  }

  const handleUpdate = (updatedRow: any) => {
    setCurrentData((prevData: any) =>
      prevData.map((row: any) =>
        row.assetEldId === updatedRow.assetEldId ? updatedRow : row
      )
    )
  }

  const handleOpen = () => {
    setIsDialogOpen(true)
  }

  const handleClose = () => {
    setCurrentData(gzcData)
    setIsDialogOpen(false)
  }

  const readOnly = isViewOnly()

  const isAgentOrCarrier = isCarrierLogin() || isAgentLogin()
  const invitingCompanyId = isAgentOrCarrier
    ? ensuredInvitingCompanyId
    : undefined
  const carrierId =
    useCarrierFilters && !isCarrierLogin()
      ? carrierData.carrierId || undefined
      : undefined
  const reloadEmbed = () => setEmbedKey((k) => k + 1)
  return (
    <>
      <ConsoleEmbed
        key={embedKey}
        reportKey={REPORT_KEYS.FLEET_RADIUS}
        invitingCompanyId={invitingCompanyId}
        carrierId={carrierId}
        slots={{
          top: (
            <RadiusOfOperationFilter
              onButtonClick={handleOpen} // GZC dialog intentionally stays active in console mode; NbROO disables it via onButtonClick={() => {}}
              reloadDashboard={reloadEmbed}
              carrierId={carrierData.carrierId}
              carrierCodeType={carrierData.carrierCodeType}
              carrierCodeValue={carrierData.carrierCodeValue}
              readOnly={readOnly}
              garagingZipcodeConfigurationEnabled={!readOnly}
            />
          ),
        }}
      />
      <GaragingZipcodeConfig
        isOpen={isDialogOpen}
        loading={loading}
        currentData={currentData}
        updateCurrentData={handleUpdate}
        onClose={handleClose}
        onSave={handleSave}
        btnLoader={saveBtnLoader}
        updateDashboardAfterUpdate={() => {
          setSaveBtnLoader(false)
          callGetGzc()
          reloadEmbed()
        }}
      />
    </>
  )
}
export default RadiusOfOperationDashboard
